import type { CompareData } from '../Compare.types';

type Translate = (key: string) => string;

export const getMoodysCompareData = (t: Translate): CompareData => ({
  competitor: "Moody's Orbis",
  competitorHeader: "Moody's · Orbis / BvD",
  competitorLogo: { type: 'logo-moodys', label: "Moody's" },
  heroTitle: "Global Database vs Moody's Orbis",
  heroDescription: (
    <>
      {t('Moody’s — through its')} <b>Orbis</b> {t('platform, formerly')} <b>Bureau van Dijk (BvD)</b>{' '}
      {t('before its 2017 acquisition — is fundamentally a')} <b>{t('data aggregator')}</b>
      {t(
        ': it licenses and blends company data from multiple third-party vendors rather than connecting directly to the source registry. Global Database sources every record directly, publishes the source, link, timestamp, and official filing behind it, and adds an ownership graph connecting 45M+ companies and 500M+ people on top.',
      )}
    </>
  ),
  quickItems: [
    <>
      <b>{t('Source, link, timestamp & filing')}</b> {t('published for every record — not a blended vendor feed')}
    </>,
    <>
      <b>{t('Flexible reseller rights')}</b> {t('— integrate, resell, or build derivative products')}
    </>,
    <>
      <b>{t('45M+ companies, 500M+ people')}</b> {t('mapped in one global ownership graph')}
    </>,
    <>
      <b>{t('M&A history, PEPs & sanctions, bank verification')}</b> {t('and people/contact data included')}
    </>,
  ],

  table: {
    title: t("Global Database vs Moody's Orbis at a glance"),
    description: t(
      "Moody's built its Orbis dataset primarily through the 2017 acquisition of Bureau van Dijk (BvD), and has continued aggregating and licensing datasets from a network of third-party providers since. Global Database sources directly and shows its work.",
    ),
    rows: [
      {
        attribute: t('Product origin'),
        gd: t('Built and maintained as a single, purpose-built platform from the ground up'),
        them: t("Orbis originates from Bureau van Dijk (BvD), acquired by Moody's in 2017"),
      },
      {
        attribute: t('Data sourcing model'),
        gd: t('Direct sourcing, with source, link, timestamp and official filing published per record'),
        them: t('Data aggregator — blends and licenses data from multiple third-party vendors'),
      },
      {
        attribute: t('Reseller rights'),
        gd: t('Flexible — enterprise clients can integrate, resell, or build derivative products'),
        them: t('Governed by vendor-level licensing terms inherited from underlying data suppliers'),
      },
      {
        attribute: t('Financials'),
        gd: t('Digitized via OCR + AI from filed statements, mapped consistently across countries'),
        them: t('Aggregated from third-party financial data providers; format varies by source'),
      },
      {
        attribute: t('Ownership graph'),
        gd: t('45M+ companies and 500M+ people connected in one global graph'),
        them: t('Ownership data assembled from multiple licensed sources'),
      },
      {
        attribute: t('M&A & deal history'),
        gd: t('Included'),
        them: t("Available via separate Moody's products/add-ons"),
      },
      {
        attribute: t('PEPs & sanctions'),
        gd: t('Included'),
        them: t('Typically offered as a separate compliance product line'),
      },
      {
        attribute: t('Bank account verification'),
        gd: t('Included'),
        them: t('Not a core part of the standard offering'),
      },
      {
        attribute: t('People & contact data'),
        gd: t('Included'),
        them: t("Limited; Moody's focus is corporate/financial risk data, not contact enrichment"),
      },
    ],
  },

  stats: {
    title: t('Why sourcing model matters'),
    description: t(
      'An aggregator is only as current and traceable as its weakest upstream vendor. Direct sourcing means every fact can be traced back to where it came from.',
    ),
    items: [
      { value: '45M+', label: t('Companies in the ownership graph') },
      { value: '500M+', label: t('People connected to companies') },
      { value: '20 yrs', label: t('Digitized financial history') },
      { value: '3–5/mo', label: t('New jurisdictions added') },
    ],
    note: t(
      "Every Global Database record carries its source, a link to it, a timestamp, and — where applicable — the official filing it was drawn from. That's a structurally different guarantee than a licensed, blended feed: it means a compliance or credit decision can point back to a primary document, not a vendor's aggregation of one.",
    ),
  },

  difference: {
    title: t("What's the difference between Global Database and Moody's Orbis?"),
    paragraphs: [
      <>
        <b>Moody&rsquo;s Orbis</b>{' '}
        {t(
          '— the company-data product most people mean when they compare "Moody’s" for company and ownership information — began life as',
        )}{' '}
        <b>Bureau van Dijk (BvD)</b>
        {t(
          ", an independent data provider that Moody's acquired in 2017 and folded into its analytics business. Since then, Orbis has continued the aggregation model BvD was built on: it licenses and blends company data from third-party providers rather than sourcing every record directly.",
        )}
      </>,
      <>
        {t(
          "That inherited model delivers real breadth, but it also means the underlying data's freshness, format, and provenance depend on whatever the licensed vendor supplies — and reselling that data onward is governed by the terms Moody's itself inherited from BvD's original licensing agreements.",
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          'takes a different starting point: data is sourced directly, with the source, link, timestamp, and official filing published against each record. On top of that direct-sourced layer, Global Database adds a global ownership graph spanning 45M+ companies and 500M+ people, OCR-and-AI-digitized financials mapped consistently across countries, M&A and deal history, PEPs and sanctions screening, bank account verification, and people/contact data — with flexible reseller rights for enterprise clients who want to build on top of it.',
        )}
      </>,
      <>
        {t(
          "For a one-off financial risk score in a market Moody's already covers well, its aggregated model is a reasonable fit. Where flexible resale rights, direct-to-source provenance, or a unified ownership graph across companies and people is the requirement, that's where Global Database is built to lead.",
        )}
      </>,
    ],
    pull: t(
      '"An aggregated dataset can only be as current as the vendor it was licensed from. A directly sourced record can always be traced back to the original filing."',
    ),
  },

  features: {
    title: t('Key features compared'),
    description: t("A closer look at what's included on each platform."),
    gd: [
      {
        title: t('Direct sourcing, fully traceable'),
        text: t('Source, link, timestamp and official filing published for every record.'),
      },
      {
        title: t('Flexible reseller rights'),
        text: t('Enterprise clients can integrate, resell, or build derivative products on the data.'),
      },
      {
        title: t('OCR + AI digitized financials'),
        text: t(
          'Financial statements extracted and mapped consistently across countries, not just where a vendor happened to supply them.',
        ),
      },
      {
        title: t('45M+ company / 500M+ people ownership graph'),
        text: t('A single graph connecting individuals to companies at global scale.'),
      },
      {
        title: t('M&A & deal history'),
        text: t('Transaction history included as part of the core company record.'),
      },
      {
        title: t('PEPs & sanctions'),
        badge: t('Included'),
        text: t('Politically exposed persons and sanctions data alongside ownership records, not a separate purchase.'),
      },
      {
        title: t('Bank account verification'),
        text: t('Verification included as part of the standard data layer.'),
      },
      {
        title: t('People & contact data'),
        text: t('Executive and decision-maker contact information included.'),
      },
    ],
    them: [
      {
        title: t('Data aggregation model'),
        text: t('Blends and licenses company and risk data from a network of third-party vendors.'),
      },
      {
        title: t('Inherited licensing terms'),
        text: t('Reseller and resale rights depend on the terms of the underlying data suppliers.'),
      },
      {
        title: t('Financial risk analytics'),
        text: t('Strong analytical and credit-risk modelling built on top of aggregated financial data.'),
      },
      {
        title: t('Ownership data via licensed sources'),
        text: t('Corporate ownership assembled from multiple acquired/licensed datasets.'),
      },
      {
        title: t('Separate product lines'),
        text: t("M&A, compliance/PEPs, and other capabilities often sold as distinct Moody's products."),
      },
      {
        title: t('Established brand & analyst network'),
        text: t('Long-standing presence in credit ratings and financial risk analysis.'),
      },
    ],
  },

  extra: {
    id: 'reseller',
    title: t('Reseller rights compared'),
    description: (
      <em>
        {t(
          'Licensing terms vary by contract and use case — confirm specifics directly with either provider. The structural difference is below.',
        )}
      </em>
    ),
    paragraphs: [
      <>
        <b>Moody&rsquo;s</b>,{' '}
        {t(
          'as an aggregator, licenses much of its underlying data from third-party vendors — which means what you can do with the data downstream is shaped by those inherited licensing terms, not a single, unified policy.',
        )}
      </>,
      <>
        <b>Global Database</b>{' '}
        {t(
          "offers flexible reseller rights on enterprise plans: clients can integrate the data into their own product, resell it, or build derivative products directly on top of it, since the data is sourced and owned by Global Database's own pipeline rather than re-licensed from elsewhere.",
        )}
      </>,
    ],
  },

  faq: {
    title: t("FAQs about Global Database vs Moody's"),
    data: [
      {
        id: 'is-moodys-a-data-aggregator',
        default_opened: true,
        question: t("Is Moody's a data aggregator?"),
        answer: t(
          "Much of Moody's company and risk-data business has grown through acquiring and licensing datasets from third-party providers, which it then packages into its own products. This gives it broad coverage, but the freshness and provenance of any given record depend on the underlying vendor it was licensed from.",
        ),
      },
      {
        id: 'can-i-resell-moodys-data',
        question: t("Can I resell Moody's data in my own product?"),
        answer: t(
          "Resale terms depend on the licensing agreements Moody's has with its underlying data vendors, and typically require specific contractual approval. Global Database offers flexible reseller rights as standard on enterprise plans, since its data is sourced directly rather than re-licensed.",
        ),
      },
      {
        id: 'does-gdb-provide-same-analytics',
        question: t("Does Global Database provide the same financial risk analytics as Moody's?"),
        answer: t(
          "Global Database digitizes financial statements using OCR and AI and maps them consistently across countries, with up to 20 years of history. Moody's has a long-standing, established credit-ratings and risk-analytics business built on top of its aggregated data. The right fit depends on whether you need direct-sourced, traceable data or established analyst-driven risk scoring.",
        ),
      },
      {
        id: 'does-gdb-include-peps-sanctions',
        question: t('Does Global Database include PEPs, sanctions, and bank account verification?'),
        answer: t(
          "Yes — all three are included as part of Global Database's standard data layer, alongside the ownership graph, rather than sold as separate add-on products.",
        ),
      },
      {
        id: 'what-is-the-ownership-graph',
        question: t('What is the ownership graph and how large is it?'),
        answer: t(
          "Global Database's ownership graph connects more than 45 million companies and 500 million people, mapping who owns and controls what across jurisdictions in a single connected structure.",
        ),
      },
      {
        id: 'is-gdb-a-good-moodys-alternative',
        question: t("Is Global Database a good Moody's alternative for compliance teams?"),
        answer: t(
          "For teams that need directly-sourced, traceable data — with source, link, timestamp, and filing published per record — plus flexible reseller rights, Global Database is commonly evaluated as a strong alternative to an aggregated data model like Moody's.",
        ),
      },
    ],
  },

  cta: {
    title: t('See Global Database on your own company records'),
    description: t(
      "Request a data review and compare sourcing, ownership resolution, and reseller terms directly against what you're using today.",
    ),
  },
});
